import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Input } from 'antd';
import {
  CalendarOutlined,
  TeamOutlined,
  CommentOutlined,
  EditOutlined,
  FilterOutlined,
  SearchOutlined,
  BgColorsOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { initials } from './teamAssistantChatUtils';
import {
  countActiveBrowseFilters,
  defaultBrowseFilters,
  quickRangePresets,
} from '../../utils/oversightFilters';
import { isBrowseActive } from '../../utils/teamAssistantBrowse';
import TeamAssistantAdvancedFiltersModal from './TeamAssistantAdvancedFiltersModal';
import TeamAssistantColorThemeModal from './TeamAssistantColorThemeModal';

const presetKeys = ['all', 'today', 'last7', 'last30', 'last90'];

const SEARCH_DEBOUNCE_MS = 280;

const TeamAssistantChatSidebar = ({
  appearance = 'default',
  filters,
  onFiltersChange,
  filterMeta,
  loading,
  collapsed,
}) => {
  const isChatGpt = appearance === 'chatgpt';
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(() => filters?.keyword ?? '');
  const staff = useMemo(() => filterMeta?.staff || [], [filterMeta?.staff]);
  const days = useMemo(() => filterMeta?.days || [], [filterMeta?.days]);
  const advancedFilterCount = countActiveBrowseFilters(filters);

  useEffect(() => {
    setSearchInput(filters?.keyword ?? '');
  }, [filters?.keyword]);

  useEffect(() => {
    const next = searchInput.trim();
    const current = (filters?.keyword || '').trim();
    if (next === current) return undefined;
    const timer = window.setTimeout(() => {
      onFiltersChange({ keyword: searchInput });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced keyword; omit onFiltersChange
  }, [searchInput, filters?.keyword]);

  const activePreset = filters?.quickPreset || 'all';
  const selectedUserId = filters?.userId;
  const selectedDay = filters?.day;

  const allChatsCount = useMemo(() => {
    if (days.length > 0) {
      return days.reduce((sum, d) => sum + (d.message_count || 0), 0);
    }
    return staff.reduce((sum, s) => sum + (s.message_count || 0), 0);
  }, [days, staff]);

  const staffFiltered = useMemo(() => {
    if (!selectedDay) return staff;
    const dayStart = moment(selectedDay).startOf('day');
    const dayEnd = moment(selectedDay).endOf('day');
    return staff.filter((s) => {
      if (!s.last_at) return false;
      const t = moment(s.last_at);
      return t.isSameOrAfter(dayStart) && t.isSameOrBefore(dayEnd);
    });
  }, [staff, selectedDay]);

  if (collapsed) {
    return null;
  }

  const clearFilters = () => {
    onFiltersChange(defaultBrowseFilters());
  };

  const sidebarClass = [
    'team-assistant-chat-sidebar',
    isChatGpt ? 'team-assistant-chat-sidebar--gpt' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={sidebarClass}>
      <TeamAssistantAdvancedFiltersModal
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        filters={filters}
        onApply={(next) => onFiltersChange(next)}
        staff={staff}
      />
      <TeamAssistantColorThemeModal open={themeModalOpen} onClose={() => setThemeModalOpen(false)} />
      <div className="team-assistant-chat-sidebar-top">
        {isChatGpt && (
          <button
            type="button"
            className="team-assistant-chat-sidebar-primary-action"
            onClick={clearFilters}
            aria-label="Show all team conversations"
          >
            <EditOutlined aria-hidden />
            <span>All conversations</span>
          </button>
        )}

        <div
          className={
            isChatGpt
              ? 'team-assistant-chat-sidebar-search-wrap'
              : 'team-assistant-chat-sidebar-search-panel'
          }
        >
          {!isChatGpt && (
            <label className="team-assistant-chat-sidebar-search-label" htmlFor="team-assistant-browse-search">
              Search
            </label>
          )}
          <Input
            id="team-assistant-browse-search"
            allowClear
            className="team-assistant-chat-sidebar-search"
            prefix={
              isChatGpt ? (
                <SearchOutlined className="team-assistant-chat-sidebar-search-icon" />
              ) : (
                <span className="team-assistant-chat-sidebar-search-prefix" aria-hidden>
                  <SearchOutlined />
                </span>
              )
            }
            placeholder={isChatGpt ? 'Search chats…' : 'Search in chat…'}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <button
          type="button"
          className={
            isChatGpt
              ? 'team-assistant-chat-sidebar-advanced-btn team-assistant-chat-sidebar-advanced-btn--gpt'
              : 'team-assistant-chat-sidebar-advanced-btn'
          }
          onClick={() => setAdvancedOpen(true)}
        >
          <FilterOutlined />
          <span>{isChatGpt ? 'Filters' : 'Advanced filters'}</span>
          <span
            className={`team-assistant-chat-sidebar-advanced-count ${
              advancedFilterCount > 0 ? 'has-value' : ''
            }`}
            aria-hidden={advancedFilterCount === 0}
          >
            {advancedFilterCount > 0 ? advancedFilterCount : ''}
          </span>
        </button>
      </div>

      <div
        className={`team-assistant-chat-sidebar-scroll ${
          loading ? 'team-assistant-chat-sidebar-scroll--loading' : ''
        }`}
      >
          <section
            className="team-assistant-chat-sidebar-section team-assistant-chat-sidebar-section--date team-assistant-chat-sidebar-card"
          >
            <div className="team-assistant-chat-sidebar-section-head">
              {!isChatGpt && (
                <CalendarOutlined className="team-assistant-chat-sidebar-section-icon" />
              )}
              <span>{isChatGpt ? 'By date' : 'Date'}</span>
            </div>
            <div className="team-assistant-chat-sidebar-presets">
              {presetKeys.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`team-assistant-chat-sidebar-pill ${
                    activePreset === key && !selectedDay ? 'is-active' : ''
                  }`}
                  onClick={() =>
                    onFiltersChange({
                      quickPreset: key,
                      dates: quickRangePresets[key].range(),
                      day: null,
                    })
                  }
                >
                  {quickRangePresets[key].label}
                </button>
              ))}
            </div>
            <div className="team-assistant-chat-sidebar-list team-assistant-chat-sidebar-list--days">
              {days.map((d) => (
                  <button
                    key={d.date}
                    type="button"
                    className={`team-assistant-chat-sidebar-row ${
                      selectedDay === d.date ? 'is-active' : ''
                    }`}
                    onClick={() =>
                      onFiltersChange({
                        quickPreset: 'custom',
                        dates: null,
                        day: d.date,
                      })
                    }
                  >
                    <span className="team-assistant-chat-sidebar-day-label">
                      {moment(d.date).format('MMM D, YYYY')}
                    </span>
                    <span className="team-assistant-chat-sidebar-count">{d.message_count}</span>
                  </button>
                ))}
            </div>
          </section>

          <section
            className="team-assistant-chat-sidebar-section team-assistant-chat-sidebar-section--team team-assistant-chat-sidebar-card"
          >
            <div className="team-assistant-chat-sidebar-section-head">
              {!isChatGpt && (
                <TeamOutlined className="team-assistant-chat-sidebar-section-icon" />
              )}
              <span>{isChatGpt ? 'Team members' : 'Team'}</span>
            </div>
            <div className="team-assistant-chat-sidebar-list team-assistant-chat-sidebar-list--staff">
              <button
                type="button"
                className={`team-assistant-chat-sidebar-row team-assistant-chat-sidebar-row--staff team-assistant-chat-sidebar-row--all ${
                  selectedUserId == null ? 'is-active' : ''
                }`}
                onClick={() => onFiltersChange({ userId: undefined })}
              >
                {!isChatGpt && (
                  <Avatar
                    size="small"
                    className="team-assistant-chat-sidebar-avatar team-assistant-chat-sidebar-avatar--all"
                    icon={<CommentOutlined />}
                  />
                )}
                <span className="team-assistant-chat-sidebar-staff-name">
                  {isChatGpt ? 'All conversations' : 'All chats'}
                </span>
                {allChatsCount > 0 && (
                  <span className="team-assistant-chat-sidebar-count">{allChatsCount}</span>
                )}
              </button>
              {staffFiltered.map((s) => {
                  const user = s.user;
                  const id = user?.id;
                  const isActive = selectedUserId != null && String(selectedUserId) === String(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`team-assistant-chat-sidebar-row team-assistant-chat-sidebar-row--staff ${
                        isActive ? 'is-active' : ''
                      }`}
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          userId: isActive ? undefined : id,
                        })
                      }
                    >
                      {!isChatGpt && (
                        <Avatar size="small" className="team-assistant-chat-sidebar-avatar">
                          {initials(user?.name)}
                        </Avatar>
                      )}
                      <span className="team-assistant-chat-sidebar-staff-name">{user?.name}</span>
                      <span className="team-assistant-chat-sidebar-count">{s.message_count}</span>
                    </button>
                  );
                })}
            </div>
          </section>
      </div>

      {!isChatGpt && (
        <div className="team-assistant-chat-sidebar-footer">
          <button
            type="button"
            className="team-assistant-chat-sidebar-clear-all"
            onClick={clearFilters}
            disabled={!isBrowseActive(filters)}
          >
            Reset all
          </button>
        </div>
      )}

      {isChatGpt && (
        <div className="team-assistant-chat-sidebar-footer team-assistant-chat-sidebar-footer--gpt">
          <button
            type="button"
            className="team-assistant-chat-sidebar-theme-btn"
            onClick={() => setThemeModalOpen(true)}
          >
            <BgColorsOutlined aria-hidden />
            <span>Color scheme</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamAssistantChatSidebar;
